import { useState } from 'react'
import { Alert, Button, Input, Spinner, Switch, Tooltip, Typography } from '@material-tailwind/react'
import { PiInfoLight } from 'react-icons/pi'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { colors } from '@material-tailwind/react/types/generic'
import Container from '../components/Container'
import GoBack from '../components/GoBack'
import { useSessionContext } from '../contexts/SessionContext'
import { useFeatureAuth } from '../hooks/authorization'
import { useFormData } from '../hooks/form'
import { useGeneralToast } from '../hooks/toast'
import { usePageTitle } from '../hooks/toolkit'
import { FEATURES } from '../utils/constants/features.constants'
import { createExperience } from '../utils/helpers/upload.helpers'
import Unauthorized from '../views/Unauthorized'
import UploadExpAdvancedSettings from './UploadExpAdvancedSettings'
import { useDarkMode } from '../contexts/DarkModeContext'

// Define the form data type inline since it's specific to this component
export interface ExperienceFormData {
  name?: string
  spots_replay?: boolean
  spots_overlap?: boolean
  enable_high_accuracy?: boolean
  default_radius?: number
  default_deadband?: number
  fade_in_time?: number
  fade_out_time?: number
  accuracy?: number
  autoplay_sounds?: boolean
}

const UploadExperience: React.FC = () => {
  usePageTitle('Create Experience | Agami')
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const { user } = useSessionContext()
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const { formData, handleDataChange, checkInputIsValid } = useFormData<ExperienceFormData>({
    spots_replay: true,
    spots_overlap: true,
    enable_high_accuracy: true,
    autoplay_sounds: false,
  })

  const navigate = useNavigate()

  const handleCreateExperience = async (e: React.FormEvent) => {
    setLoading(true)

    e.preventDefault()
    e.stopPropagation()

    const {
      name,
      spots_replay,
      spots_overlap,
      enable_high_accuracy,
      default_radius,
      default_deadband,
      fade_in_time,
      fade_out_time,
      accuracy,
      autoplay_sounds,
    } = formData

    const insertData = {
      name,
      spots_replay,
      spots_overlap,
      enable_high_accuracy,
      autoplay_sounds,
      ...(accuracy && { accuracy }),
      ...(default_radius && { default_radius }),
      ...(default_deadband && { default_deadband }),
      ...(fade_in_time && { fade_in_time }),
      ...(fade_out_time && { fade_out_time }),
      owner_id: user?.id,
    }

    setLoading(false)

    const { data, error } = await createExperience({ insertData })

    if (error) {
      toastError('Something went wrong')
      setLoading(false)
      return
    }

    toastSuccess('Experience created succesfully')
    setLoading(false)
    setSuccess(true)
    navigate(`/experiences/${data?.id}`)
  }

  const expAuth = useFeatureAuth(FEATURES.EXP_EDITOR_UI)

  if (!expAuth) {
    return (
      <Container direction="col">
        <Unauthorized />
      </Container>
    )
  }

  return (
    <Container direction="col">
      <div className="mb-2 self-start">
        <GoBack />
      </div>
      <Typography
        variant="lead"
        className="text-primary dark:text-white"
        data-testid="create-new-experience-title"
      >
        {t('upload.experience.createTitle')}
      </Typography>

      {success && (
        <div className="mt-8 text-center">
          <Alert color="green" variant="ghost">
            <Typography>
              {t('upload.experience.createdSuccess')}{' '}
              <span className="font-bold underline">
                <Link to="/experiences">{t('upload.experience.experiencesDashboard')}</Link>
              </span>{' '}
              {t('upload.experience.addAgamiAndShare')}
            </Typography>
          </Alert>
        </div>
      )}

      {!success && (
        <form
          className="w-full md:w-1/2 lg:w-2/3 flex flex-col gap-4 my-10"
          onSubmit={handleCreateExperience}
        >
          <Input
            type="text"
            label={t('upload.experience.name')}
            value={formData.name ?? ''}
            onChange={(e) => handleDataChange('name', e.currentTarget.value)}
            required
            data-testid="create-new-experience-name-input"
            labelProps={{
              className:
                'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
            }}
          />

          <div className="flex justify-start items-center gap-2">
            <Switch
              color={(darkMode ? 'accent' : 'primary') as colors}
              id="spots_replay_switch"
              label={t('upload.experience.agamiReplay')}
              checked={Boolean(formData.spots_replay)}
              onChange={(e) => handleDataChange('spots_replay', e.currentTarget.checked)}
            />
            <Tooltip
              content={
                <div className="w-[300px]">
                  <Typography variant="small" className="font-bold">
                    {t('upload.experience.agamiReplayDescription')}
                  </Typography>
                  <Typography variant="small">
                    <strong>
                      <em>{t('upload.experience.defaultTrue')}</em>
                    </strong>
                  </Typography>
                </div>
              }
              placement="right"
              interactive
            >
              <span>
                <PiInfoLight className="text-primary dark:text-white" />
              </span>
            </Tooltip>
          </div>

          <div className="flex justify-start items-center gap-2">
            <Switch
              color={(darkMode ? 'accent' : 'primary') as colors}
              id="spots_overlap_switch"
              label={t('upload.experience.agamiOverlap')}
              checked={Boolean(formData.spots_overlap)}
              onChange={(e) => handleDataChange('spots_overlap', e.currentTarget.checked)}
            />
            <Tooltip
              content={
                <div className="w-[300px]">
                  <Typography variant="small" className="font-bold">
                    {t('upload.experience.agamiOverlapDescription')}
                  </Typography>
                  <Typography variant="small">
                    <strong>
                      <em>{t('upload.experience.defaultTrue')}</em>
                    </strong>
                  </Typography>
                </div>
              }
              placement="right"
              interactive
            >
              <span>
                <PiInfoLight className="text-primary dark:text-white" />
              </span>
            </Tooltip>
          </div>

          <div className="flex justify-start items-center gap-2">
            <Switch
              color={(darkMode ? 'accent' : 'primary') as colors}
              id="autoplay_sounds_switch"
              label={t('upload.experience.autoplaySounds')}
              checked={Boolean(formData.autoplay_sounds)}
              onChange={(e) => handleDataChange('autoplay_sounds', e.currentTarget.checked)}
            />
            <Tooltip
              content={
                <div className="w-[300px]">
                  <Typography variant="small" className="font-bold">
                    {t('upload.experience.autoplaySoundsDescription')}
                  </Typography>
                  <Typography variant="small">
                    <strong>
                      <em>{t('upload.experience.defaultFalse')}</em>
                    </strong>
                  </Typography>
                </div>
              }
              placement="right"
              interactive
            >
              <span>
                <PiInfoLight className="text-primary dark:text-white" />
              </span>
            </Tooltip>
          </div>

          {formData.autoplay_sounds && (
            <Alert variant={darkMode ? 'outlined' : 'ghost'} color="orange">
              <small>{t('upload.experience.autoplaySoundsWarning')}</small>
            </Alert>
          )}

          {/* advanced settings */}
          <UploadExpAdvancedSettings
            formData={formData}
            handleDataChange={handleDataChange}
            checkInputIsValid={checkInputIsValid}
          />

          <Button type="submit" disabled={loading} data-testid="create-new-experience-btn">
            {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('upload.experience.createButton')}
          </Button>
        </form>
      )}
    </Container>
  )
}

export default UploadExperience
